import { Container, Group, LoadingContainer, Text } from '@melio/penny';
import { FundingSource, FundingSourceType } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect, useState } from 'react';

import { CardFundingSourceFormProps } from '../../CardFundingSourceForm/CardFundingSourceForm';
import { FundingSourceCard } from '../../FundingSourceCard/FundingSourceCard';
import { FundingSourceCardGroupList } from '../../FundingSourceCard/FundingSourceCardGroupList';
import { AddNewFundingSourceView } from './AddNewFundingSourceView';

export type SubscriptionCheckoutFundingSourceSelectionProps = CardFundingSourceFormProps & {
  fundingSources: FundingSource[] | undefined;
  isLoading: boolean;
  newFundingSourceId?: string;
  onAddBankAccountClicked: VoidFunction;
};

type PaymentMethodView = 'selected' | 'all' | 'add-new';

export const SubscriptionPaymentMethod = ({
  onSubmit,
  isLoading,
  fundingSources = [],
  newFundingSourceId,
  onAddBankAccountClicked,
  ...props
}: SubscriptionCheckoutFundingSourceSelectionProps) => {
  const { formatMessage } = useMelioIntl();
  const [fundingSourceType, setFundingSourceType] = useState<FundingSourceType>(FundingSourceType.Card);
  const [view, setView] = useState<PaymentMethodView>(fundingSources.length ? 'selected' : 'add-new');
  const [selectedFundingSource, setSelectedFundingSource] = useState<FundingSource | undefined>(
    fundingSources.find((f) => f.id === newFundingSourceId) ?? fundingSources[0]
  );

  useEffect(() => {
    if (fundingSources[0]) {
      handleSelectFundingSource(fundingSources[0]);
    }
  }, [fundingSources]);

  const handleSelectFundingSource = (fundingSource: FundingSource) => {
    setSelectedFundingSource(fundingSource);
    setView('selected');
  };

  const handleNewCardSelection = () => {
    setFundingSourceType(FundingSourceType.Card);
    setView('add-new');
  };

  const handleNewBankAccountSelection = () => {
    setFundingSourceType(FundingSourceType.BankAccount);
    startNewBankAccountCreation();
  };

  const startNewBankAccountCreation = () => {
    onAddBankAccountClicked();
  };

  const getView = () => {
    switch (view) {
      case 'all':
        return (
          <FundingSourceCardGroupList
            fundingSources={fundingSources}
            onSelected={handleSelectFundingSource}
            selectedId={selectedFundingSource?.id}
            onAddBank={onAddBankAccountClicked}
            onAddCard={() => setView('add-new')}
            onClose={() => setView('selected')}
          />
        );
      case 'add-new':
        return (
          <AddNewFundingSourceView
            fundingSourceType={fundingSourceType}
            onSubmit={onSubmit}
            onAddBank={handleNewBankAccountSelection}
            onAddCard={handleNewCardSelection}
            onClose={() => setView('selected')}
            hasFundingSources={!!fundingSources.length}
            {...props}
          />
        );
      default:
        return selectedFundingSource ? (
          <FundingSourceCard fundingSource={selectedFundingSource} onChangeClick={() => setView('all')} />
        ) : null;
    }
  };

  return (
    <Group variant="vertical" spacing="xxxs" data-component="SubscriptionPaymentMethod">
      <Container paddingBottom="s">
        <Text as="h2" textStyle="body2Semi" color="neutral.darker">
          {formatMessage('activities.subscription.checkout.paymentMethod.title')}
        </Text>
      </Container>
      <LoadingContainer isLoading={isLoading}>{getView()}</LoadingContainer>
    </Group>
  );
};
